/**
 * useFloorWheel (web) — standard desktop canvas navigation:
 *
 * - Ctrl+wheel / Cmd+wheel (and trackpad pinch, which browsers deliver as
 *   ctrlKey+wheel) → zoom about the CURSOR, exponential steps.
 * - Plain wheel / trackpad two-finger scroll → pan (both axes; Shift with
 *   a vertical-only wheel swaps it to horizontal, the mouse convention).
 *
 * The listener is non-passive and preventDefaults every wheel inside the
 * canvas — the canvas owns the wheel there (stops page scroll + browser
 * page-zoom), everything outside scrolls normally. Camera writes go
 * straight to the shared values (zero React renders); cull/LOD settle is
 * debounced to wheel-idle, mirroring the gesture-settle contract.
 *
 * @module floorplan/hooks/useFloorWheel
 * @category FloorPlan
 */

import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { ViewInstance } from 'react-native';

import { zoomAboutPoint } from '../utils/camera';
import { applyCameraState, cameraSnapshot } from '../viewport/useFloorCamera';
import type useFloorEditor from './useFloorEditor';

type FloorEditor = ReturnType<typeof useFloorEditor>;

/** Exponential zoom sensitivity per wheel deltaY unit (Figma-like feel). */
const ZOOM_INTENSITY = 0.01;
/** Wheel-idle window before the cull/LOD settle runs. */
const SETTLE_DEBOUNCE_MS = 160;

const useFloorWheel = (
  editor: FloorEditor,
  containerRef: RefObject<ViewInstance | null>,
): void => {
  const editorRef = useRef(editor);
  // Assigned post-commit (not during render) to satisfy react-hooks/refs.
  useEffect(() => {
    editorRef.current = editor;
  });

  useEffect(() => {
    // react-native-web resolves a View ref to its backing HTMLElement.
    const element = containerRef.current as unknown as HTMLElement | null;
    if (element === null || typeof element.addEventListener !== 'function') {
      return undefined;
    }

    const settle = { timer: null as ReturnType<typeof setTimeout> | null };
    const scheduleSettle = () => {
      if (settle.timer !== null) clearTimeout(settle.timer);
      settle.timer = setTimeout(() => {
        const live = editorRef.current;
        live.handleSettle(cameraSnapshot(live.camera));
      }, SETTLE_DEBOUNCE_MS);
    };

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const live = editorRef.current;
      const snap = cameraSnapshot(live.camera);

      if (event.ctrlKey || event.metaKey) {
        // Zoom about the cursor (trackpad pinch arrives as ctrl+wheel).
        const rect = element.getBoundingClientRect();
        const focal = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        };
        const factor = Math.exp(-event.deltaY * ZOOM_INTENSITY);
        const next = zoomAboutPoint(snap, focal, snap.zoom * factor);
        applyCameraState(live.camera, next);
        scheduleSettle();
        return;
      }

      // Pan. Trackpads send both axes; a mouse wheel sends deltaY only,
      // where Shift swaps it to horizontal (browser/Figma convention).
      let dx = event.deltaX;
      let dy = event.deltaY;
      if (event.shiftKey && dx === 0) {
        dx = dy;
        dy = 0;
      }
      applyCameraState(live.camera, {
        tx: snap.tx - dx,
        ty: snap.ty - dy,
        zoom: snap.zoom,
      });
      scheduleSettle();
    };

    element.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      element.removeEventListener('wheel', onWheel);
      if (settle.timer !== null) clearTimeout(settle.timer);
    };
  }, [containerRef]);
};

export default useFloorWheel;
